/**
 * Given an object where each key is a plan name and each value is
 * an array of assistants, this function builds a flat array of strings:
 *
 *   [
 *     '<labelPrefix> – subscriber',
 *     'prompt 1 from basic…',
 *     'prompt 2 from basic…',
 *     '<labelPrefix> – premium',
 *     'prompt 1 from premium…',
 *     '<labelPrefix> – patron',
 *     'prompt 1 from plus…',
 *     …
 *   ]
 *
 * @param {Record<string, any[]>} groupedAssistants
 *    Example:
 *    {
 *      basic:   [ { sample_prompts: ['a','b'], … }, … ],
 *      premium: [ { sample_prompts: ['x','y'], … }, … ],
 *      plus:    [ { sample_prompts: ['p','q'], … }, … ],
 *      …
 *    }
 * @param {string} labelPrefix – e.g. 'Personality'
 * @returns {string[]} An array where each plan is introduced by
 *    `<labelPrefix> – <mappedPlanName>` and followed by all sample_prompts for that plan.
 */
export function extractPromptsFromGrouped(
  groupedAssistants: Record<string, any[]>,
  labelPrefix: string
): string[] {
  const prompts: string[] = [];
  const planLabelMap: Record<string, string> = {
    basic: "subscriber",
    plus: "patron",
  };

  // Define the exact order we want for plan display names
  const planOrder = ["subscriber", "patron", "premium"];

  // Create an array of [rawPlanName, displayName] pairs
  const planEntries = Object.entries(groupedAssistants).map(([rawPlanName]) => {
    const displayName = planLabelMap[rawPlanName] || rawPlanName;
    return [rawPlanName, displayName];
  });

  // Sort plans according to our custom order
  planEntries.sort((a, b) => {
    const indexA = planOrder.indexOf(a[1]);
    const indexB = planOrder.indexOf(b[1]);

    // Handle plans in our ordered list
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // Alphabetical fallback for other plans
    return a[1].localeCompare(b[1]);
  });

  // Process plans in sorted order
  for (const [rawPlanName, displayPlanName] of planEntries) {
    prompts.push(`${labelPrefix} – ${displayPlanName}`);

    groupedAssistants[rawPlanName].forEach((assistant) => {
      (assistant.sample_prompts || []).forEach((prompt: string) => {
        prompts.push(prompt);
      });
    });
  }

  return prompts;
}

/**
 * (No change needed here—just included for completeness.)
 * Groups a flat array of assistants into an object by their for_plan array.
 */
export function groupByForPlan(assistants: any[]): Record<string, any[]> {
  return (assistants || []).reduce(
    (acc: Record<string, any[]>, assistant: any) => {
      (assistant.for_plan || []).forEach((planName: string) => {
        if (!acc[planName]) {
          acc[planName] = [];
        }
        acc[planName].push(assistant);
      });
      return acc;
    },
    {}
  );
}
